import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import useUsers from "../../../hooks/useUsers";
import AppContext from "../../../AppContext";
import List, { STATE } from "../../List";
import SearchBox from "../../common/SearchBox";
import Button from "../../common/Button";
import { ReactComponent as PencilSvg } from "../../../assets/admin/icons/pencil.svg";
import { ReactComponent as TrashSvg } from "../../../assets/admin/icons/trash.svg";
import { ReactComponent as MessageSvg } from "../../../assets/profile/message.svg";
import { ReactComponent as UserSvg } from "../../../assets/icons/user.svg";
import AddUser from "./AddUser";
import _ from "lodash";
import { toast } from "react-toastify";
import ChatSessionContext from "../../../contexts/ChatSessionContext";
import SecondaryPagination from "../../SecondaryPagination/SecondaryPagination";

const assetsUri = import.meta.env.ASSETS_URI;

const availableColumns = [
  {
    display: "Name",
    key: "name",
    textAlign: "left",
  },
  {
    display: "Email",
    key: "email",
    textAlign: "left",
  },
  {
    display: "Role",
    key: "role",
    textAlign: "left",
  },
  {
    display: "Actions",
    key: "actions",
    textAlign: "left",
    maxWidth: 200,
  },
];

const Members = ({ id: companyId }) => {
  const { user: loggedInUser, isAllowed, plans } = useContext(AppContext);
  const { startChat } = useContext(ChatSessionContext);
  const { queryUsers, queryRoles, patchUser } = useUsers();
  const [dataState, setDataState] = useState(STATE.LOADING);
  const [users, setUsers] = useState([]);
  const [isOwnerCompany, setIsOwnerCompany] = useState(false);
  const [toggleModal, setToggleModal] = useState(false);
  const [filter, setFilter] = useState([]);
  const [columns, setColumns] = useState([]);
  const [isGuest] = useState(companyId !== loggedInUser?.company);
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [metadata, setMetadata] = useState(undefined);

  useEffect(() => {
    // * Remove actions column if not allowed
    let _columns = _.cloneDeep(availableColumns);

    if (!isAllowed("CM-01-0000", "update")) {
      _columns = _columns.filter((c) => c.key !== "actions");
    }

    // * Modify columns if is guest
    if (isGuest) {
      _columns = _columns.filter(
        (c) => !["email", "role", "actions"].includes(c.key)
      );

      _columns.push({
        display: "",
        key: "guestActions",
        textAlign: "left",
        maxWidth: 200,
      });
    }

    setColumns(_columns);

    fetchData();
  }, [filter, page, limit]);

  const fetchData = async () => {
    if (loggedInUser?.company === companyId) setIsOwnerCompany(true);

    // * Fetch users
    const { data: users, _metadata } = await queryUsers(
      {
        $and: [{ company: companyId }, { deleted: false }, ...filter],
      },
      page,
      limit,
      { avatar: 1, firstname: 1, surname: 1, email: 1, roles: 1 }
    );

    // * Get roles
    let _roles = [];

    if (!isGuest) {
      const { data: queryRoleResults } = await queryRoles({
        _id: {
          $in: users
            .map((u) => (u.roles.length > 0 ? u.roles[0] : undefined))
            .filter((r) => !!r),
        },
      });

      _roles = queryRoleResults;
    }

    // * Map roles and add actions based on permission
    const _users = users.map((u) => {
      // * Name
      u.name = (
        <span className="profile-members-lists-info">
          <img
            className="profile-members-lists-info__avatar"
            alt="avatar"
            src={`${assetsUri}/${u.avatar}`}
            onError={({ target }) => {
              if (!u.avatar) return;
              target.src = u.avatar;
            }}
          />
          {`${u.firstname} ${u.surname ?? ""}`}
        </span>
      );

      if (isGuest) {
        u.guestActions = (
          <div className="profile-followers-list-user-actions">
            <span
              className="profile-followers-list-user-actions__item"
              onClick={(e) => {
                e.stopPropagation();

                handleChatClick(u._id);
              }}
            >
              <MessageSvg height={22} />
              Chat
            </span>
          </div>
        );
      }

      if (!isGuest) {
        let _role = undefined;

        // * Role mapping
        if (u.roles.length > 0) {
          _role = _roles.find((r) => r._id === u.roles[0])?.name;

          const classNames = [
            "profile-members-lists-role",
            `profile-members-lists-role--${_role.toLowerCase()}`,
          ];

          u.role = <span className={classNames.join(" ")}>{_role}</span>;
        }

        // * Only allow actions if not logged in user
        if (_role !== "Owner" && loggedInUser._id !== u._id) {
          u.actions = (
            <div className="profile-members-lists-actions">
              <div
                className="profile-members-lists-actions__action"
                onClick={() => handleUpdateUserClick(u._id)}
              >
                <PencilSvg />
              </div>
              <div
                className="profile-members-lists-actions__action"
                onClick={() => handleDeleteUserClick(u._id)}
              >
                <TrashSvg />
              </div>
            </div>
          );
        }
      }

      return u;
    });

    setUsers(_users);
    setMetadata(_metadata);
    setDataState(STATE.IDLE);
  };

  const handleChatClick = (userId) => {
    startChat(userId, loggedInUser);
  };

  const handleSearch = (value) => {
    if (!value) {
      setFilter([]);
      return;
    }

    setFilter([
      {
        $or: [
          { firstname: { $regex: value } },
          { surname: { $regex: value } },
          { email: { $regex: value } },
        ],
      },
    ]);
  };

  const handleAddUserClick = async () => {
    setToggleModal(true);
  };

  const handleUpdateUserClick = async (userId) => {
    setToggleModal(userId);
  };

  const handleDeleteUserClick = async (userId) => {
    if (!window.confirm("Remove user from the company?")) {
      return;
    }

    // * Get guest plan id
    const guestPlan = plans.find((p) => p.name === "Guest");

    // * Assign plan to user and remove company value
    try {
      await patchUser(userId, {
        plan: guestPlan._id,
        company: null,
      });

      fetchData();
      toast.info("Removed user from company");
    } catch (error) {
      toast.error("Unable to remove user");
      console.error(error.message);
    }
  };

  return (
    <div className="profile-members">
      <div className="profile-members-header">
        <SearchBox placeholder="Search email or name" onChange={handleSearch} />
        {isAllowed("CM-01-0000", "create") && (
          <Button label={"Add User"} onClick={handleAddUserClick} />
        )}
      </div>
      <List
        className="profile-members-list"
        contentState={dataState}
        columns={columns}
        dataSource={users}
      >
        <SecondaryPagination
          limits={[10, 50]}
          defaultPage={page}
          defaultLimit={limit}
          pages={metadata?.totalPages ?? 1}
          onPageChange={(_) => setPage(_)}
          onLimitChange={(_) => setLimit(_)}
        />
      </List>
      {toggleModal && (
        <AddUser
          userId={typeof toggleModal === "string" ? toggleModal : undefined}
          onClose={() => {
            setToggleModal(false);
            fetchData();
          }}
        />
      )}
    </div>
  );
};

Members.propTypes = {};

export default Members;
